import * as THREE from 'three';
import React from 'react';
import { correctAxisData } from '../../utils';
import * as constantsViewer from '../../constants';
import data from './data/wheel.json';

export function Wheel({
  wheelPosition = 'FL',
  cabinetHeight = 590,
  cabinetWidth = 1000,
  cabinetDepth = 420,
  unselected = false,
}) {
  var modelCabinetWidth = 999;
  var modelCabinetDepth = 419;
  var modelCabinetHeight = 540;

  var cabinetDiffWith = cabinetWidth - modelCabinetWidth;
  var cabinetDiffDepth = cabinetDepth - modelCabinetDepth;

  let xDiff = 0;
  let yDiff = 0;
  let zDiff = 0;

  yDiff -= (cabinetHeight - modelCabinetHeight) / 2;
  switch (wheelPosition) {
    case 'FL':
      xDiff -= modelCabinetWidth + cabinetDiffWith / 2 - 90;
      zDiff += cabinetDiffDepth / 2;
      break;

    case 'FR':
      xDiff += cabinetDiffWith / 2;
      zDiff += cabinetDiffDepth / 2;
      break;

    case 'BL':
      zDiff -= cabinetDepth - 2;
      zDiff += cabinetDiffDepth / 2 + 115;
      xDiff -= modelCabinetWidth + cabinetDiffWith / 2 - 90;
      break;

    case 'BR':
      xDiff += cabinetDiffWith / 2;
      zDiff -= cabinetDepth - 2;
      zDiff += cabinetDiffDepth / 2 + 115;
      break;

    case 'F':
      xDiff -= modelCabinetWidth + cabinetDiffWith / 2 - 40 - cabinetWidth / 2;
      zDiff += cabinetDiffDepth / 2;
      break;

    case 'B':
      zDiff -= cabinetDepth - 2;
      zDiff += cabinetDiffDepth / 2 + 115;
      xDiff -= modelCabinetWidth + cabinetDiffWith / 2 - 40 - cabinetWidth / 2;
      break;

    default:
      break;
  }

  return (
    <group>
      <MountingBase
        xDiff={xDiff}
        yDiff={yDiff}
        zDiff={zDiff}
        unselected={unselected}
      />
      <ScrewMountingBase
        xDiff={xDiff}
        yDiff={yDiff}
        zDiff={zDiff}
        unselected={unselected}
      />
      <ScrewMountingBase
        xDiff={xDiff + 43}
        yDiff={yDiff}
        zDiff={zDiff + 43}
        unselected={unselected}
      />
      <group>
        <RotatingPortion
          xDiff={xDiff}
          yDiff={yDiff}
          zDiff={zDiff}
          unselected={unselected}
        />
        <Fork
          xDiff={xDiff}
          yDiff={yDiff}
          zDiff={zDiff}
          unselected={unselected}
        />
        <WheelOuter
          xDiff={xDiff}
          yDiff={yDiff}
          zDiff={zDiff}
          unselected={unselected}
        />
        <WheelInner
          xDiff={xDiff}
          yDiff={yDiff}
          zDiff={zDiff}
          unselected={unselected}
        />
        <Axle
          xDiff={xDiff}
          yDiff={yDiff}
          zDiff={zDiff}
          rotation={[0, 0, 0]}
          unselected={unselected}
        />
        <Axle
          xDiff={xDiff}
          yDiff={yDiff}
          zDiff={zDiff + 32}
          rotation={[0, Math.PI, 0]}
          unselected={unselected}
        />
      </group>
    </group>
  );
}

export function WheelsBasePlate({
  cabinetHeight = 590,
  cabinetWidth = 1000,
  cabinetDepth = 420,
  color = 'W',
  unselected = false,
}) {
  var modelCabinetHeight = 540;
  var modelCabinetDepth = 419;
  var modelCabinetWidth = 1000;
  var scaleX = cabinetWidth / modelCabinetWidth;
  var scaleZ = cabinetDepth / modelCabinetDepth;

  scaleX = (cabinetWidth - 6) / 1000;
  scaleZ = (419 * scaleZ + 1) / 420;
  var vertices = new Float32Array(data.wheelsBasePlate.vertices);
  var normals = new Float32Array(data.wheelsBasePlate.normals);

  var uv_coord = new Float32Array(data.wheelsBasePlate.uv_coord);

  /* 
            Remove this in meshPhongMaterial because onClick fired twice:
              side={THREE.DoubleSide} 
            */

  /*
            Other method to apply lookAt method on camera:
              const { camera } = useThree()
              camera.lookAt(new THREE.Vector3(0.00000, 0.00000, 0.00000)) 
            */
  return (
    <mesh
      receiveShadow
      castShadow
      onUpdate={self => {
        var center = new THREE.Vector3();
        self.geometry.computeBoundingBox();
        self.geometry.boundingBox.getCenter(center);
        self.geometry.center();
        self.position.copy(center);

        self.geometry.scale(scaleX, 1, scaleZ);
        self.position.x -= 2.1;
        self.position.y -= (cabinetHeight - modelCabinetHeight) / 2;
      }}
    >
      <bufferGeometry attach="geometry">
        <bufferAttribute
          attachObject={['attributes', 'position']}
          count={vertices.length / 3}
          array={correctAxisData(vertices)}
          itemSize={3}
        />
        <bufferAttribute
          attachObject={['attributes', 'normal']}
          count={normals.length / 3}
          array={correctAxisData(normals)}
          itemSize={3}
        />
        <bufferAttribute
          attachObject={['attributes', 'uv']}
          count={uv_coord.length / 2}
          array={uv_coord}
          itemSize={2}
        />
      </bufferGeometry>
      <meshPhongMaterial
        attach="material"
        color={color === 'B' ? 0x000000 : 0xffffff}
        specular={0x919191}
        shininess={4.92458}
        opacity={unselected ? constantsViewer.OPACITY_UNSELECTED_SHELF : 1}
        transparent
      />
    </mesh>
  );
}

function Fork({ xDiff = 0, yDiff = 0, zDiff = 0, unselected = false }) {
  var vertices = new Float32Array(data.fork.vertices);
  var normals = new Float32Array(data.fork.normals);

  var uv_coord = new Float32Array(data.fork.uv_coord);

  /* 
        Remove this in meshPhongMaterial because onClick fired twice:
          side={THREE.DoubleSide} 
        */

  /*
        Other method to apply lookAt method on camera:
          const { camera } = useThree()
          camera.lookAt(new THREE.Vector3(0.00000, 0.00000, 0.00000)) 
        */
  return (
    <mesh
      receiveShadow
      castShadow
      onUpdate={self => {
        var center = new THREE.Vector3();
        self.geometry.computeBoundingBox();
        self.geometry.boundingBox.getCenter(center);
        self.geometry.center();
        self.position.copy(center);

        self.position.x += xDiff;
        self.position.y += yDiff;
        self.position.z += zDiff;
      }}
    >
      <bufferGeometry attach="geometry">
        <bufferAttribute
          attachObject={['attributes', 'position']}
          count={vertices.length / 3}
          array={correctAxisData(vertices)}
          itemSize={3}
        />
        <bufferAttribute
          attachObject={['attributes', 'normal']}
          count={normals.length / 3}
          array={correctAxisData(normals)}
          itemSize={3}
        />
        <bufferAttribute
          attachObject={['attributes', 'uv']}
          count={uv_coord.length / 2}
          array={uv_coord}
          itemSize={2}
        />
      </bufferGeometry>
      <meshPhongMaterial
        attach="material"
        color={0x64666a}
        specular={0xffffff}
        shininess={50}
        opacity={unselected ? constantsViewer.OPACITY_UNSELECTED_SHELF : 1}
        transparent
      />
    </mesh>
  );
}

function RotatingPortion({
  xDiff = 0,
  yDiff = 0,
  zDiff = 0,
  unselected = false,
}) {
  var vertices = new Float32Array(data.rotatingPortion.vertices);
  var normals = new Float32Array(data.rotatingPortion.normals);

  var uv_coord = new Float32Array(data.rotatingPortion.uv_coord);

  /* 
          Remove this in meshPhongMaterial because onClick fired twice:
            side={THREE.DoubleSide} 
          */

  /*
          Other method to apply lookAt method on camera:
            const { camera } = useThree()
            camera.lookAt(new THREE.Vector3(0.00000, 0.00000, 0.00000)) 
          */
  return (
    <mesh
      receiveShadow
      castShadow
      onUpdate={self => {
        var center = new THREE.Vector3();
        self.geometry.computeBoundingBox();
        self.geometry.boundingBox.getCenter(center);
        self.geometry.center();
        self.position.copy(center);

        self.position.x += xDiff;
        self.position.y += yDiff;
        self.position.z += zDiff;
      }}
    >
      <bufferGeometry attach="geometry">
        <bufferAttribute
          attachObject={['attributes', 'position']}
          count={vertices.length / 3}
          array={correctAxisData(vertices)}
          itemSize={3}
        />
        <bufferAttribute
          attachObject={['attributes', 'normal']}
          count={normals.length / 3}
          array={correctAxisData(normals)}
          itemSize={3}
        />
        <bufferAttribute
          attachObject={['attributes', 'uv']}
          count={uv_coord.length / 2}
          array={uv_coord}
          itemSize={2}
        />
      </bufferGeometry>
      <meshPhongMaterial
        attach="material"
        color={0x64666a}
        specular={0xffffff}
        shininess={50}
        opacity={unselected ? constantsViewer.OPACITY_UNSELECTED_SHELF : 1}
        transparent
      />
    </mesh>
  );
}

function MountingBase({ xDiff = 0, yDiff = 0, zDiff = 0, unselected = false }) {
  var vertices = new Float32Array(data.mountingBase.vertices);
  var normals = new Float32Array(data.mountingBase.normals);

  var uv_coord = new Float32Array(data.mountingBase.uv_coord);

  /* 
          Remove this in meshPhongMaterial because onClick fired twice:
            side={THREE.DoubleSide} 
          */

  /*
          Other method to apply lookAt method on camera:
            const { camera } = useThree()
            camera.lookAt(new THREE.Vector3(0.00000, 0.00000, 0.00000)) 
          */
  return (
    <mesh
      receiveShadow
      castShadow
      onUpdate={self => {
        var center = new THREE.Vector3();
        self.geometry.computeBoundingBox();
        self.geometry.boundingBox.getCenter(center);
        self.geometry.center();
        self.position.copy(center);

        self.position.x += xDiff;
        self.position.y += yDiff;
        self.position.z += zDiff;
      }}
    >
      <bufferGeometry attach="geometry">
        <bufferAttribute
          attachObject={['attributes', 'position']}
          count={vertices.length / 3}
          array={correctAxisData(vertices)}
          itemSize={3}
        />
        <bufferAttribute
          attachObject={['attributes', 'normal']}
          count={normals.length / 3}
          array={correctAxisData(normals)}
          itemSize={3}
        />
        <bufferAttribute
          attachObject={['attributes', 'uv']}
          count={uv_coord.length / 2}
          array={uv_coord}
          itemSize={2}
        />
      </bufferGeometry>
      <meshPhongMaterial
        attach="material"
        color={0x64666a}
        specular={0xffffff}
        shininess={50}
        opacity={unselected ? constantsViewer.OPACITY_UNSELECTED_SHELF : 1}
        transparent
      />
    </mesh>
  );
}

function Axle({
  xDiff = 0,
  yDiff = 0,
  zDiff = 0,
  rotation = [0, 0, 0],
  unselected = false,
}) {
  var vertices = new Float32Array(data.leftAxle.vertices);
  var normals = new Float32Array(data.leftAxle.normals);

  var uv_coord = new Float32Array(data.leftAxle.uv_coord);

  /* 
          Remove this in meshPhongMaterial because onClick fired twice:
            side={THREE.DoubleSide} 
          */

  /*
          Other method to apply lookAt method on camera:
            const { camera } = useThree()
            camera.lookAt(new THREE.Vector3(0.00000, 0.00000, 0.00000)) 
          */
  return (
    <mesh
      receiveShadow
      castShadow
      onUpdate={self => {
        var center = new THREE.Vector3();
        self.geometry.computeBoundingBox();
        self.geometry.boundingBox.getCenter(center);
        self.geometry.center();
        self.position.copy(center);

        self.position.x += xDiff;
        self.position.y += yDiff;
        self.position.z += zDiff;
      }}
      rotation={rotation}
    >
      <bufferGeometry attach="geometry">
        <bufferAttribute
          attachObject={['attributes', 'position']}
          count={vertices.length / 3}
          array={correctAxisData(vertices)}
          itemSize={3}
        />
        <bufferAttribute
          attachObject={['attributes', 'normal']}
          count={normals.length / 3}
          array={correctAxisData(normals)}
          itemSize={3}
        />
        <bufferAttribute
          attachObject={['attributes', 'uv']}
          count={uv_coord.length / 2}
          array={uv_coord}
          itemSize={2}
        />
      </bufferGeometry>
      <meshPhongMaterial
        attach="material"
        color={0x64666a}
        specular={0xffffff}
        shininess={50}
        opacity={unselected ? constantsViewer.OPACITY_UNSELECTED_SHELF : 1}
        transparent
      />
    </mesh>
  );
}

function ScrewMountingBase({
  xDiff = 0,
  yDiff = 0,
  zDiff = 0,
  unselected = false,
}) {
  var vertices = new Float32Array(data.leftScrewMountingBase.vertices);
  var normals = new Float32Array(data.leftScrewMountingBase.normals);

  var uv_coord = new Float32Array(data.leftScrewMountingBase.uv_coord);

  /* 
          Remove this in meshPhongMaterial because onClick fired twice:
            side={THREE.DoubleSide} 
          */

  /*
          Other method to apply lookAt method on camera:
            const { camera } = useThree()
            camera.lookAt(new THREE.Vector3(0.00000, 0.00000, 0.00000)) 
          */
  return (
    <mesh
      receiveShadow
      castShadow
      onUpdate={self => {
        var center = new THREE.Vector3();
        self.geometry.computeBoundingBox();
        self.geometry.boundingBox.getCenter(center);
        self.geometry.center();
        self.position.copy(center);

        self.position.x += xDiff;
        self.position.y += yDiff;
        self.position.z += zDiff;
      }}
    >
      <bufferGeometry attach="geometry">
        <bufferAttribute
          attachObject={['attributes', 'position']}
          count={vertices.length / 3}
          array={correctAxisData(vertices)}
          itemSize={3}
        />
        <bufferAttribute
          attachObject={['attributes', 'normal']}
          count={normals.length / 3}
          array={correctAxisData(normals)}
          itemSize={3}
        />
        <bufferAttribute
          attachObject={['attributes', 'uv']}
          count={uv_coord.length / 2}
          array={uv_coord}
          itemSize={2}
        />
      </bufferGeometry>
      <meshPhongMaterial
        attach="material"
        color={0x64666a}
        specular={0xffffff}
        shininess={50}
        opacity={unselected ? constantsViewer.OPACITY_UNSELECTED_SHELF : 1}
        transparent
      />
    </mesh>
  );
}

function WheelOuter({ xDiff = 0, yDiff = 0, zDiff = 0, unselected = false }) {
  var vertices = new Float32Array(data.wheelOuter.vertices);
  var normals = new Float32Array(data.wheelOuter.normals);

  var uv_coord = new Float32Array(data.wheelOuter.uv_coord);

  /* 
          Remove this in meshPhongMaterial because onClick fired twice:
            side={THREE.DoubleSide} 
          */

  /*
          Other method to apply lookAt method on camera:
            const { camera } = useThree()
            camera.lookAt(new THREE.Vector3(0.00000, 0.00000, 0.00000)) 
          */
  return (
    <mesh
      receiveShadow
      castShadow
      onUpdate={self => {
        var center = new THREE.Vector3();
        self.geometry.computeBoundingBox();
        self.geometry.boundingBox.getCenter(center);
        self.geometry.center();
        self.position.copy(center);

        self.position.x += xDiff;
        self.position.y += yDiff;
        self.position.z += zDiff;
      }}
    >
      <bufferGeometry attach="geometry">
        <bufferAttribute
          attachObject={['attributes', 'position']}
          count={vertices.length / 3}
          array={correctAxisData(vertices)}
          itemSize={3}
        />
        <bufferAttribute
          attachObject={['attributes', 'normal']}
          count={normals.length / 3}
          array={correctAxisData(normals)}
          itemSize={3}
        />
        <bufferAttribute
          attachObject={['attributes', 'uv']}
          count={uv_coord.length / 2}
          array={uv_coord}
          itemSize={2}
        />
      </bufferGeometry>
      <meshPhongMaterial
        attach="material"
        color={0x9cb6be}
        specular={0x000000}
        shininess={2}
        opacity={unselected ? constantsViewer.OPACITY_UNSELECTED_SHELF : 1}
        transparent
      />
    </mesh>
  );
}

function WheelInner({ xDiff = 0, yDiff = 0, zDiff = 0, unselected = false }) {
  var vertices = new Float32Array(data.wheelInner.vertices);
  var normals = new Float32Array(data.wheelInner.normals);

  var uv_coord = new Float32Array(data.wheelInner.uv_coord);

  /* 
          Remove this in meshPhongMaterial because onClick fired twice:
            side={THREE.DoubleSide} 
          */

  /*
          Other method to apply lookAt method on camera:
            const { camera } = useThree()
            camera.lookAt(new THREE.Vector3(0.00000, 0.00000, 0.00000)) 
          */
  return (
    <mesh
      receiveShadow
      castShadow
      onUpdate={self => {
        var center = new THREE.Vector3();
        self.geometry.computeBoundingBox();
        self.geometry.boundingBox.getCenter(center);
        self.geometry.center();
        self.position.copy(center);

        self.position.x += xDiff;
        self.position.y += yDiff;
        self.position.z += zDiff;
      }}
    >
      <bufferGeometry attach="geometry">
        <bufferAttribute
          attachObject={['attributes', 'position']}
          count={vertices.length / 3}
          array={correctAxisData(vertices)}
          itemSize={3}
        />
        <bufferAttribute
          attachObject={['attributes', 'normal']}
          count={normals.length / 3}
          array={correctAxisData(normals)}
          itemSize={3}
        />
        <bufferAttribute
          attachObject={['attributes', 'uv']}
          count={uv_coord.length / 2}
          array={uv_coord}
          itemSize={2}
        />
      </bufferGeometry>
      <meshPhongMaterial
        attach="material"
        color={0x181818}
        specular={0x505050}
        shininess={2}
        opacity={unselected ? constantsViewer.OPACITY_UNSELECTED_SHELF : 1}
        transparent
      />
    </mesh>
  );
}
